import { createRoute, useNavigate, useParams } from "@tanstack/react-router";
import { ClubGuard } from "@/components/ClubGuard";
import {
  useClubDetails,
  useCreateGathering,
  type GatheringDetail,
} from "@/lib/clubs/gatherings";
import { GatheringForm } from "@/components/gatherings/GatheringForm";
import { NotLocalClubNotice } from "@/components/gatherings/NotLocalClubNotice";
import { clubsRoute } from "./clubs";
import styles from "./clubs-gatherings-routes.module.scss";

function NewGathering() {
  const params = useParams({ from: clubsGatheringsNewRoute.id });
  const clubId = params.id;
  const navigate = useNavigate();

  const { data: club, isLoading: clubLoading } = useClubDetails(clubId);
  const createGathering = useCreateGathering(clubId);

  if (clubLoading && !club) {
    return (
      <div role="status" aria-live="polite" style={{ padding: 32 }}>
        Loading…
      </div>
    );
  }

  if (club && !club.isLocalClub) {
    return <NotLocalClubNotice clubName={club.name} />;
  }

  return (
    <div className={styles.shell}>
      <header className={styles.head}>
        <p className={styles.eyebrow}>{club?.name ?? "Club"} · Gatherings</p>
        <h1 className={styles.title}>Schedule a gathering</h1>
        <p className={styles.sub}>
          Set the time and place. Members will be notified.
        </p>
      </header>
      <GatheringForm
        mode="create"
        readingRooms={club?.readingRooms ?? []}
        isReadingRoomsLoading={clubLoading}
        isSubmitting={createGathering.isPending}
        submitError={createGathering.error}
        clubDiscoverable={Boolean(club?.discoverable)}
        onSubmit={(payload) => {
          createGathering.mutate(payload, {
            onSuccess: (created: GatheringDetail) => {
              navigate({
                to: "/clubs/$id/gatherings/$gid",
                params: { id: clubId, gid: created.id },
              });
            },
          });
        }}
        onDiscard={() =>
          navigate({
            to: "/clubs/$id/gatherings",
            params: { id: clubId },
          })
        }
      />
    </div>
  );
}

export const clubsGatheringsNewRoute = createRoute({
  getParentRoute: () => clubsRoute,
  path: "/$id/gatherings/new",
  component: () => (
    <ClubGuard requireClubId>
      <NewGathering />
    </ClubGuard>
  ),
});
